import React, { useState, useEffect, useCallback } from 'react';

/**
 * RelativeTime component that displays a relative time string (e.g. "5m ago") 
 * with a tooltip showing the exact timestamp, and auto-refreshes periodically.
 * 
 * @param {string} timestamp - ISO timestamp string to format
 * @param {boolean} showEditedPrefix - If true, adds "(edited " before the time
 * @param {number} refreshInterval - Milliseconds between refreshes
 */
const RelativeTime = ({ timestamp, showEditedPrefix = false, refreshInterval = 30000 }) => {
  const [relativeTime, setRelativeTime] = useState('');
  const [fullDateTime, setFullDateTime] = useState('');
  
  // Update both relative and full time formats
  const updateTimeDisplay = useCallback(() => {
    if (!timestamp) {
      setRelativeTime('');
      setFullDateTime('');
      return;
    }
    
    // Handle both formats: milliseconds since epoch or ISO string
    const date = typeof timestamp === 'number' 
      ? new Date(timestamp) // Milliseconds since epoch (new format)
      : new Date(timestamp); // ISO string (old format)
    
    const now = new Date();
    
    // Calculate time difference in milliseconds
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHr / 24);
    
    // Set relative time
    let relativeStr = '';
    if (diffSec < 60) {
      relativeStr = 'just now';
    } else if (diffMin < 60) {
      relativeStr = `${diffMin}m ago`;
    } else if (diffHr < 24) {
      relativeStr = `${diffHr}h ago`;
    } else if (diffDays < 7) {
      relativeStr = `${diffDays}d ago`;
    } else {
      relativeStr = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      }).format(date);
    }
    
    if (showEditedPrefix) {
      relativeStr = `(edited ${relativeStr})`;
    }
    
    setRelativeTime(relativeStr);
    
    // Set full datetime for tooltip with explicit timezone information
    setFullDateTime(new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true,
      timeZoneName: 'short' // Add timezone name (like "PST")
    }).format(date));
  }, [timestamp, showEditedPrefix]);
  
  // Set up interval to refresh the relative time
  useEffect(() => {
    // Skip if timestamp is empty
    if (!timestamp) return;
    
    // Initial update
    updateTimeDisplay();
    
    // Set up refresh interval
    const intervalId = setInterval(updateTimeDisplay, refreshInterval);
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [timestamp, refreshInterval, updateTimeDisplay]);
  
  // If timestamp is empty, don't render anything
  if (!timestamp) return null;
  
  return (
    <span className="group relative inline-block whitespace-nowrap">
      <span>{relativeTime}</span>
      <span className="absolute z-10 -mt-1 w-auto px-2 py-1 text-xs bg-gray-800 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-75 -left-[50%] -translate-x-[50%] bottom-full mb-1 pointer-events-none">
        {fullDateTime}
      </span>
    </span>
  );
};

export default RelativeTime;